// Chat message types for EmpowerIQ streaming chat

export type {
    ElementExplainContext,
    AppSection,
} from './explainUtils'

export type ChatMode = 'docs' | 'agent'

export interface ToolCall {
    callId: string
    name: string
    status: 'running' | 'completed' | 'failed'
}

export interface ProvenanceStep {
    step: 'user-provided' | 'x-default' | 'x-normalize' | 'x-infer' | 'x-suggest' | 'llm-generated'
    ok: boolean
    inputs?: Record<string, unknown>
    output: unknown
}

export interface ToolReceipt {
    execution?: {
        tool: string
        version: string
        id: string
        timestamp: string
        correlation_id: string
    }
    parameters?: {
        finalized?: Record<string, unknown>
        redacted?: string[]
        provenance?: Record<string, ProvenanceStep[]>
    }
    result?: { success: boolean; error: string | null }
}

/** Rich per-tool execution record populated from SSE frames */
export interface ToolExecution {
    call_id: string
    name: string
    arguments: Record<string, unknown>
    status: 'pending' | 'executing' | 'completed' | 'failed'
    /** Unix timestamp (ms) when the tool was invoked */
    timestamp: number
    /** Unix timestamp (ms) when the tool finished */
    endTimestamp?: number
    result?: { result: unknown; success: boolean; receipt?: ToolReceipt }
    error?: string
    tool_selection_reason?: string
}

/** Agent metadata received via the `meta` SSE frame */
export interface StreamingMeta {
    agentId?: string
    agentName?: string
    model?: string
    toolNames?: string[]
    toolCount?: number
    tier?: string
    reasoningEffort?: string
}

/** Progress payload from `progress` / `heartbeat` SSE frames */
export interface StreamingProgress {
    status?: string
    elapsed_ms?: number
    iteration?: number
    max_iterations?: number
    current_tool?: string | null
    tools_completed?: string[]
    tools_pending?: string[]
    estimated_remaining_ms?: number
    model?: string
    reasoning_effort?: string
}

/** Estimate payload from `estimate` SSE frame */
export interface StreamingEstimate {
    estimatedMs?: number
    confidence?: string
    message?: string
}

/** Structured error from `error` SSE frame */
export interface StreamingError {
    type: string
    message: string
    code: string
    recoverable: boolean
    retryAfterMs?: number
}

/** WAITING form field from `awaiting_input` SSE frame */
export interface WaitingFormField {
    name: string
    type: string
    description: string
    required: boolean
    default_value?: unknown
    suggestions?: string[]
    dynamic_suggest?: {
        tool: string
        args?: Record<string, unknown>
        map?: Record<string, string>
        min_chars?: number
        limit?: number
    }
    validation?: { pattern?: string; message?: string }
    error?: string
    current_value?: unknown
    readOnly?: boolean
    redacted?: boolean
    needs_confirmation?: boolean
    validation_errors?: string[]
    source?: string
    confidence?: number
    format?: string
}

export interface URLElicitationEntry {
    mode: string
    elicitationId: string
    url: string
    message: string
}

export interface WaitingFormConfirmation {
    tool_name?: string
    confirm_message?: string
    impact_description?: string
    risk_level?: string
    proposed_args?: Record<string, unknown>
    display_params?: Array<{ label: string; value: string; section: string; path?: string }>
    assumptions?: string[]
}

/** Data for `awaiting_input` interactive forms */
export interface WaitingFormData {
    task_id: string
    fields: WaitingFormField[]
    tool_name?: string
    original_request?: string
    resume_endpoint?: string
    resume_token?: string
    mode?: 'form' | 'url'
    elicitations?: URLElicitationEntry[]
    prompt?: string
    confirmation?: WaitingFormConfirmation
}

/** Output card from `output_card` SSE frame */
export interface OutputCardData {
    card_type: 'evidence' | 'digest' | 'score' | string
    card_id: string
    payload: Record<string, unknown>
    created_at?: string
    sentinel_id?: string
}

export interface TimeoutInfo {
    message: string
    suggestions: string[]
    canRetry: boolean
    partialResults: unknown[]
}

export interface ExtractedEntities {
    emails?: string[]
    inferredNames?: Array<{ source?: string; first?: string; last?: string }>
    dates?: string[]
    identifiers?: string[]
    urls?: string[]
    message?: string
}

/** Downloadable artifact metadata from `artifact` SSE frame */
export interface ArtifactData {
    artifact_id: string
    message_id: string
    tool_call_id?: string
    filename: string
    mime_type: string
    download_url: string
    size_bytes: number
    checksum_sha256?: string
    expires_at: string
    classification?: string
    display_inline?: boolean
    inline_preview_url?: string
    description?: string
}

export interface ChatMessage {
    id: string
    role: 'user' | 'assistant'
    content: string
    timestamp: Date
    isStreaming?: boolean
    isRouterBubble?: boolean
    isCollapsed?: boolean
    /** User message image previews (data URLs) */
    images?: string[]
    /** Agent mode only: MCP tool calls triggered during this response */
    toolCalls?: ToolCall[]
    /** Agent mode only: rich per-tool execution records */
    toolExecutions?: ToolExecution[]
    /** Agent mode only: metadata from the meta frame */
    streamingMeta?: StreamingMeta
    /** Live progress from progress/heartbeat frames */
    streamingProgress?: StreamingProgress
    /** Time estimate from estimate frame */
    streamingEstimate?: StreamingEstimate
    /** Structured streaming error */
    streamingError?: StreamingError
    /** Downloadable outputs from artifact frames */
    artifacts?: ArtifactData[]
    /** Agent run id when provided by backend */
    runId?: string
    /** Elapsed ms since the assistant message was created (updated while streaming) */
    elapsedMs?: number
    /** Agent is waiting for user input (form or URL elicitation) */
    isAwaitingInput?: boolean
    waitingForm?: WaitingFormData
    /** Inline output card message */
    messageType?: 'text' | 'output_card' | 'waiting_form'
    outputCard?: OutputCardData
    /** Agent reasoning (non-L0 tiers) */
    rationale?: string
    agentTier?: string
    /** Entity extraction preview */
    extractedEntities?: ExtractedEntities
    /** Timeout details from final frame with reason=timeout */
    timeoutInfo?: TimeoutInfo
}

export interface Assistant {
    id: string // VectorStoreId
    name: string // FriendlyName
    icon?: string
    role?: string
}

export interface RouterResponse {
    VectorStoreId?: string
    Confidence?: number
    Reasoning?: string
    Answer?: string
    Name?: string
}

export interface ChatMetrics {
    total: number
    success: number
    error: number
}

export type ConnectionStatus =
    | 'connecting'
    | 'connected'
    | 'disconnected'
    | 'error'
    | 'invalid_config'

export interface ChatState {
    messages: ChatMessage[]
    isLoading: boolean
    connectionStatus: ConnectionStatus
    selectedAssistant: Assistant | null
    assistants: Assistant[]
    metrics: ChatMetrics
    error: string | null
}

// SignalR event handlers
export interface SignalREventHandlers {
    onMessageReceived: (message: string) => void
    onCompleteMessage: (message: string) => void
    onReceiveMetrics: (total: number, success: number, error: number) => void
    onAssistantSelected: (assistantId: string, assistantName: string) => void
    onStartRouterBubble: () => void
    onReceiveFriendlyNames: (json: string) => void
}

// DOMPurify configuration for sanitizing HTML
export const DOMPURIFY_CONFIG = {
    ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'u',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'ul',
        'ol',
        'li',
        'blockquote',
        'code',
        'pre',
        'a',
        'div',
        'span',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'id'],
    ALLOW_DATA_ATTR: false,
}
